import { useMemo } from 'react';
import { Box, BoxProps, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ScheduledActionFragmentFragment } from '../../../../api/gql';
import { ExpandedBox } from '../../../../components/ExpandedBox';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { ActionIcon } from '../../../../components/invoice/ScheduledActionIcon';
import { ActionName } from '../../../../components/invoice/ScheduledActionName';
import { BooleanIndicator, WaitOrDoneIndicator } from '../../../../components/table/BooleanIndicator';
import { renderCellExpand } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { dateGridColDef } from '../../../../components/table/utils/columnHeader';

type Props = {
  scheduledActions?: ScheduledActionFragmentFragment[];
  isLoading?: boolean;
  isError?: boolean;
  transNS?: string;
} & BoxProps;

export const ScheduledActionsTableBox = ({ scheduledActions, isError, isLoading, transNS, sx, ...rest }: Props) => {
  const { t } = useTranslation(transNS);
  return (
    <Box sx={{ position: 'relative', minHeight: 270, ...sx }} {...rest}>
      {isLoading ? <FullSizeLoader /> : null}
      {isError ? (
        <ExpandedBox sx={{ backdropFilter: 'blur(2px)', zIndex: 10 }}>
          <Typography>{t('query.error')}</Typography>
        </ExpandedBox>
      ) : null}
      <Box>
        {scheduledActions ? (
          <InvoiceScheduledActionsTable scheduledActions={scheduledActions} transNS={transNS} />
        ) : null}
      </Box>
    </Box>
  );
};

const InvoiceScheduledActionsTable = ({
  scheduledActions,
  transNS,
}: {
  scheduledActions: ScheduledActionFragmentFragment[];
  transNS?: string;
}) => {
  const { t } = useTranslation(transNS);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'plannedAt',
        headerName: t('scheduledActions.plannedAt') as string,
        minWidth: 100,
        maxWidth: 150,
        flex: 1,
        ...dateGridColDef,
      },
      {
        field: 'action',
        headerName: t('scheduledActions.action') as string,
        minWidth: 80,
        maxWidth: 100,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction={'row'} gap={1} alignItems={'center'}>
            <ActionIcon action={params.value} color={'action'} fontSize={'small'} />
            <ActionName action={params.value} color={'action'} fontSize={'small'} />
          </Stack>
        ),
      },
      {
        field: 'state',
        headerName: t('scheduledActions.state') as string,
        flex: 1,
        maxWidth: 80,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.doneAt) {
            return <BooleanIndicator value={true} />;
          }
          if (params.row.cancelledAt) {
            return <BooleanIndicator value={false} />;
          }
          return <WaitOrDoneIndicator value={params.value} />;
        },
      },
      {
        field: 'recommendedMessage',
        headerName: t('scheduledActions.recommendedMessage') as string,
        flex: 1,
        minWidth: 300,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
    ],
    [t],
  );

  const rows = useMemo(
    () =>
      scheduledActions
        .sort((a, b) => {
          if (a.scheduledAt < b.scheduledAt) return -1;
          return 1;
        })
        .map((task) => {
          return {
            id: task.id,
            plannedAt: task.scheduledAt,
            cancelledAt: task.cancelledAt,
            doneAt: task.doneAt,
            action: task.action,
            state: task.doneAt !== null || task.doneAt !== undefined ? !!task.doneAt : task.doneAt,
            recommendedMessage: task.recommendedMessage,
          };
        }) ?? [],
    [scheduledActions],
  );

  return (
    <Table
      id={'scheduled-actions'}
      columns={columns}
      rows={rows}
      density={'compact'}
      sx={{
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
          py: '22px',
        },
      }}
    />
  );
};
